import { Theme, ThemeProvider } from "@mui/material";
import { ComponentsProps } from "@mui/material/styles";
import type {} from "@mui/x-charts/themeAugmentation";
import { PropsWithChildren, ReactElement } from "react";

// Stops Localize from collecting phrases in popups built on `MuiPopper`, such as
// tooltips. x-charts tooltips are not among them: they render inside the chart.
const getMuiPopperContainer = (): HTMLElement => {
  const containerId = "mui-popper-container";
  const container = document.getElementById(containerId);

  if (container === null) {
    const newContainer = document.createElement("div");
    newContainer.id = containerId;
    newContainer.dataset.notranslate = "true";

    document.body.appendChild(newContainer);

    return newContainer;
  }

  return container;
};

// x-charts reads theme default props for the layer container but leaves it out of its `Components` typing,
// and the props type has no `data-*` keys although they reach the root `<div>`.
declare module "@mui/material/styles" {
  interface Components {
    MuiChartsLayerContainer?: {
      defaultProps?: ComponentsProps["MuiChartsLayerContainer"] & { "data-notranslate"?: "true" };
    };
  }
}

const withNoTranslateDefaults = (outerTheme: Theme): Theme => ({
  ...outerTheme,
  components: {
    ...outerTheme.components,
    MuiPopper: { defaultProps: { container: getMuiPopperContainer } },
    // The chart layer holds the plot, the tooltip and the accessibility proxy, which writes the focused item's
    // name and value as text for screen readers. Legends render outside it.
    MuiChartsLayerContainer: { defaultProps: { "data-notranslate": "true" } },
  },
});

export const NoTranslateTheme = ({ children }: PropsWithChildren): ReactElement => (
  <ThemeProvider theme={withNoTranslateDefaults}>{children}</ThemeProvider>
);
